import { ScaffoldModule, ScaffoldConfig, InstallResult, FileChange, Framework } from './types';
import { FileOperations } from './files';
import { ASTEngine } from './ast';
import { TemplateEngine } from './templates';
import { join, dirname } from 'path';

export class ScaffoldExecutor {
  /**
   * Execute scaffold installation
   */
  static async executeScaffold(
    scaffold: ScaffoldModule,
    config: ScaffoldConfig,
    projectRoot: string
  ): Promise<InstallResult> {
    const result: InstallResult = {
      success: true,
      filesChanged: [],
      dependencies: {
        dev: [],
        prod: []
      },
      errors: [],
      warnings: []
    };

    try {
      // Get project info
      const projectInfo = await FileOperations.detectProject(projectRoot);
      
      // Execute scaffold installation and capture the result
      const installResult = await scaffold.install({ ...config, projectInfo });
      
      // Process filesChanged array to actually create/modify files
      if (installResult.filesChanged && installResult.filesChanged.length > 0) {
        for (const fileChange of installResult.filesChanged) {
          try {
            const filePath = join(projectRoot, fileChange.path);
            
            if (fileChange.action === 'created') {
              // Ensure directory exists
              const dir = dirname(filePath);
              if (!(await FileOperations.fileExists(dir))) {
                const { mkdirSync } = await import('fs');
                mkdirSync(dir, { recursive: true });
              }
              
              // Write file content
              await FileOperations.writeFile(filePath, fileChange.content || '');
            } else if (fileChange.action === 'modified') {
              // For modified files, we need to read existing content and apply changes
              // This is more complex and would require AST operations
              // For now, we'll treat it as a write operation
              await FileOperations.writeFile(filePath, fileChange.content || '');
            }
          } catch (fileError) {
            installResult.errors.push(`Failed to ${fileChange.action} file ${fileChange.path}: ${fileError instanceof Error ? fileError.message : 'Unknown error'}`);
          }
        }
      }
      
      return installResult;
    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      return result;
    }
  }

  /**
   * Get preview of scaffold changes
   */
  static async getScaffoldPreview(
    scaffold: ScaffoldModule,
    config: ScaffoldConfig,
    projectRoot: string
  ): Promise<FileChange[]> {
    if (!scaffold.getPreview) {
      return [];
    }

    try {
      const projectInfo = await FileOperations.detectProject(projectRoot);
      return await scaffold.getPreview({ ...config, projectInfo });
    } catch (error) {
      console.error('Failed to get preview:', error);
      return [];
    }
  }

  /**
   * Create file from template
   */
  static async createFileFromTemplate(
    filePath: string,
    templateName: string,
    variables: Record<string, any>
  ): Promise<boolean> {
    try {
      const content = TemplateEngine.generateFileContent(templateName, variables);
      return await FileOperations.writeFile(filePath, content);
    } catch (error) {
      console.error(`Failed to create file ${filePath}:`, error);
      return false;
    }
  }

  /**
   * Modify existing file with AST operations
   */
  static async modifyFileWithAST(
    filePath: string,
    modifications: any[]
  ): Promise<boolean> {
    try {
      return await ASTEngine.applyModifications(filePath, modifications);
    } catch (error) {
      console.error(`Failed to modify file ${filePath}:`, error);
      return false;
    }
  }

  /**
   * Add dependency to project
   */
  static async addDependency(
    projectRoot: string,
    name: string,
    version: string,
    isDev = false
  ): Promise<boolean> {
    return await FileOperations.addDependency(projectRoot, name, version, isDev);
  }

  /**
   * Add script to package.json
   */
  static async addScript(
    projectRoot: string,
    name: string,
    command: string
  ): Promise<boolean> {
    return await FileOperations.addScript(projectRoot, name, command);
  }

  /**
   * Install dependencies using package manager
   */
  static async installDependencies(projectRoot: string, packageManager?: string): Promise<boolean> {
    const { exec } = await import('child_process');
    const { promisify } = await import('util');
    const execAsync = promisify(exec);

    try {
      // Detect package manager if not provided
      const pm = packageManager || await this.detectPackageManager(projectRoot);
      
      const command = pm === 'pnpm' ? 'pnpm install' : 
                     pm === 'yarn' ? 'yarn install' : 
                     'npm install';
      
      await execAsync(command, { cwd: projectRoot });
      return true;
    } catch (error) {
      console.error('Failed to install dependencies:', error);
      return false;
    }
  }

  /**
   * Detect package manager from lock files
   */
  static async detectPackageManager(projectRoot: string): Promise<string> {
    const { existsSync } = await import('fs');
    const { resolve } = await import('path');
    
    if (existsSync(resolve(projectRoot, 'yarn.lock'))) {
      return 'yarn';
    } else if (existsSync(resolve(projectRoot, 'pnpm-lock.yaml'))) {
      return 'pnpm';
    } else {
      return 'npm'; // Default to npm
    }
  }

  /**
   * Get framework-specific configuration paths
   */
  static getFrameworkConfigPaths(framework: Framework): Record<string, string> {
    const configPaths: Record<string, Record<string, string>> = {
      nextjs: {
        configDir: 'config/',
        srcDir: 'src/',
        pagesDir: 'pages/',
        appDir: 'app/',
        componentsDir: 'components/',
        libDir: 'lib/',
        publicDir: 'public/'
      },
      'react-vite': {
        configDir: 'config/',
        srcDir: 'src/',
        componentsDir: 'src/components/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      },
      'react-cra': {
        configDir: 'config/',
        srcDir: 'src/',
        componentsDir: 'src/components/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      },
      vue: {
        configDir: 'config/',
        srcDir: 'src/',
        componentsDir: 'src/components/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      },
      angular: {
        configDir: 'config/',
        srcDir: 'src/',
        componentsDir: 'src/app/components/',
        libDir: 'src/lib/',
        publicDir: 'src/assets/'
      },
      svelte: {
        configDir: 'config/',
        srcDir: 'src/',
        componentsDir: 'src/lib/',
        libDir: 'src/lib/',
        publicDir: 'static/'
      },
      express: {
        configDir: 'config/',
        srcDir: 'src/',
        routesDir: 'src/routes/',
        middlewareDir: 'src/middleware/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      },
      fastify: {
        configDir: 'config/',
        srcDir: 'src/',
        routesDir: 'src/routes/',
        pluginsDir: 'src/plugins/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      },
      nodejs: {
        configDir: 'config/',
        srcDir: 'src/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      },
      unknown: {
        configDir: 'config/',
        srcDir: 'src/',
        libDir: 'src/lib/',
        publicDir: 'public/'
      }
    };

    return configPaths[framework] || configPaths.unknown;
  }

  /**
   * Get framework-specific file extensions
   */
  static getFrameworkExtensions(framework: Framework): Record<string, string> {
    const extensions: Record<string, Record<string, string>> = {
      nextjs: {
        component: '.tsx',
        page: '.tsx',
        api: '.ts',
        config: '.js'
      },
      'react-vite': {
        component: '.tsx',
        page: '.tsx',
        config: '.js'
      },
      'react-cra': {
        component: '.tsx',
        page: '.tsx',
        config: '.js'
      },
      vue: {
        component: '.vue',
        page: '.vue',
        config: '.js'
      },
      angular: {
        component: '.ts',
        page: '.ts',
        config: '.js'
      },
      svelte: {
        component: '.svelte',
        page: '.svelte',
        config: '.js'
      },
      express: {
        route: '.ts',
        middleware: '.ts',
        config: '.js'
      },
      fastify: {
        route: '.ts',
        plugin: '.ts',
        config: '.js'
      },
      nodejs: {
        module: '.ts',
        config: '.js'
      },
      unknown: {
        module: '.ts',
        config: '.js'
      }
    };

    return extensions[framework] || extensions.unknown;
  }

  /**
   * Get framework-specific dependency recommendations
   */
  static getFrameworkDependencies(framework: Framework): Record<string, string[]> {
    const dependencies: Record<string, Record<string, string[]>> = {
      nextjs: {
        dev: ['@types/react', '@types/node', 'eslint', 'prettier'],
        prod: ['react', 'react-dom', 'next']
      },
      'react-vite': {
        dev: ['@types/react', '@types/node', 'eslint', 'prettier', 'vite'],
        prod: ['react', 'react-dom']
      },
      'react-cra': {
        dev: ['@types/react', '@types/node', 'eslint', 'prettier'],
        prod: ['react', 'react-dom', 'react-scripts']
      },
      vue: {
        dev: ['@types/node', 'eslint', 'prettier', 'vite'],
        prod: ['vue']
      },
      angular: {
        dev: ['@types/node', 'eslint', 'prettier'],
        prod: ['@angular/core', '@angular/cli']
      },
      svelte: {
        dev: ['@types/node', 'eslint', 'prettier', 'vite'],
        prod: ['svelte']
      },
      express: {
        dev: ['@types/express', '@types/node', 'eslint', 'prettier'],
        prod: ['express']
      },
      fastify: {
        dev: ['@types/node', 'eslint', 'prettier'],
        prod: ['fastify']
      },
      nodejs: {
        dev: ['@types/node', 'eslint', 'prettier'],
        prod: []
      },
      unknown: {
        dev: ['@types/node', 'eslint', 'prettier'],
        prod: []
      }
    };

    return dependencies[framework] || dependencies.unknown;
  }
}

